import { useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import SentimentsSvc from '../../../../../services/dbServices/SentimentsSvc';
import EntitiesSvc from '../../../../../services/dbServices/EntitiesSvc';
import convertPermalinkToPath from '../../../../../helpers/urlHelpers';
import { GOLD } from '../../../../../data/permissions';

import Preloader from '../../../../Preloader';
import Tooltip from '../../../../Tooltip';
import CFLink from '../../../../Shared/CFLink';

import Styles from '../styles.module.scss';

const cx = classNames.bind(Styles);

const WorldSentiments = (props) => {
  const { title, icon, activePeriod } = props;
  const [locationList, setLocationList] = useState({
    data: [],
    loading: true,
  });
  const isGoldPlan = useSelector(({ subscriptions }) => (
    subscriptions.permissions?.access_levels?.includes(GOLD)
  ));
  const width = useSelector(({ common }) => (
    common.width
  ));

  const callListingsAPI = async (params) => {
    try {
      const { data } = await SentimentsSvc.getListingsSentiments(params);
      return data;
    } catch (e) {
      console.log(e);
    }
    return { };
  };

  const generateSubLocationsData = async () => {
    setLocationList({
      data: [],
      loading: true,
    });

    const params = {
      entity_type: 'Country',
      per_page: 5,
      sorting: 'descending',
      sentiment_filter_mode: 'categories',
      period: activePeriod,
    };

    if (title === 'Negative' || title === 'Very Negative') {
      params.sorting = 'ascending';
    }
    const data = await callListingsAPI({
      ...params,
      sentiment_categories: title,
    });

    setLocationList({
      data: data.list || [],
      loading: false,
    });
  };

  useEffect(() => {
    generateSubLocationsData();
  }, []);

  useEffect(() => {
    generateSubLocationsData();
  }, [activePeriod]);

  const history = useHistory();
  const onPermalinkClick = (e, selectedLocation) => {
    if (selectedLocation.permalink) {
      return;
    }

    e.preventDefault();
    EntitiesSvc.getEntityPermalink({
      entityId: selectedLocation.entity_id,
      entityType: selectedLocation.entity_type,
    }).then(({ permalink }) => {
      if (permalink) {
        selectedLocation.permalink = permalink;
        history.push(convertPermalinkToPath(permalink));
      }
    });
  };

  return (
    <div className={Styles['locations-box-sentiment']}>
      <div className={Styles['locations-box-sentiment-title']}>{title}</div>
      <div className={Styles['locations-box-sentiment-lists']}>
        {locationList.loading ? (
          <Preloader loading top="unset" />
        ) : (
          <>
            {!locationList.data.length && (
              <div className={Styles['locations-box-sentiment-no-data']}>
                —
              </div>
            )}
            {locationList.data.map((list, i) => (
              <LocationListComponent
                key={list.entity_id}
                list={list}
                index={i}
                icon={icon}
                onPermalinkClick={onPermalinkClick}
                isGoldPlan={isGoldPlan}
                width={width}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

const LocationListComponent = ({
  list, index, icon, onPermalinkClick, isGoldPlan, width,
}) => {
  const pageLink = list?.permalink ? convertPermalinkToPath(list?.permalink) : '#';

  return (
    <div
      key={list.entity_id}
      className={Styles['locations-box-sentiment-list']}
    >
      {(index === 0 || index === 1) && !isGoldPlan ? (
        <Tooltip
          className={cx('locations-box-tooltip')}
          tooltipClassName={Styles['locations-box-tooltip-content']}
          renderOutside
          contentStyle={{
            transform: width >= 2800 ? 'translate(-70px, -120px)' : 'translate(-36px, -54px)',
          }}
          allowTransformReplace={false}
          hasBigScreenDesign
          icon={(
            <div className={cx('locations-box-sentiment-list-name', 'blur')}>
              Country
            </div>
          )}
        >
          <CFLink to="/pricing" external={false}>
            Upgrade
          </CFLink>
          &nbsp;for more comprehensive and more granular sentiment analysis.
        </Tooltip>
      ) : (
        <Link
          to={pageLink}
          className={Styles['locations-box-sentiment-list-name']}
          onClick={(e) => onPermalinkClick(e, list)}
        >
          {list.entity_name}
        </Link>
      )}
      <div className={Styles['locations-box-sentiment-list-right']}>
        <FontAwesomeIcon icon={icon} className={icon.iconName} />
        <div className={Styles['locations-box-sentiment-list-value']}>
          {list.sentiment}
        </div>
      </div>
    </div>
  );
};

export default WorldSentiments;
